<script setup>
import { computed } from 'vue'

const props = defineProps({
  modelValue: {
    type: [Boolean, Number, String],
    default: null,
  },
  options: {
    type: Array,
    required: true,
  },
})

const emit = defineEmits(['update:modelValue'])

const activeIndex = computed(() => Math.max(0, props.options.findIndex(option => option.value === props.modelValue)))
</script>
<template>
  <div
    class="segmented-toggle"
    role="radiogroup"
    :style="{ '--segment-count': options.length, '--segment-index': activeIndex }"
  >
    <span class="segmented-toggle__thumb" />
    <button
      v-for="option in options"
      :key="String(option.value)"
      type="button"
      role="radio"
      class="segmented-toggle__option"
      :class="{ 'segmented-toggle__option--active': option.value === modelValue }"
      :aria-checked="option.value === modelValue"
      @click="emit('update:modelValue', option.value)"
    >
      <q-icon v-if="option.icon" :name="option.icon" size="xs" />
      <span>{{ option.label }}</span>
    </button>
  </div>
</template>
<style scoped lang="scss">
.segmented-toggle {
  position: relative;
  display: grid;
  grid-template-columns: repeat(var(--segment-count), 1fr);
  padding: 0.25rem;
  border: 1px solid color-mix(in srgb, var(--q-primary) 15%, transparent);
  border-radius: 0.75rem;
  background: color-mix(in srgb, var(--q-primary) 5%, transparent);
  isolation: isolate;
}

.segmented-toggle__thumb {
  position: absolute;
  top: 0.25rem;
  bottom: 0.25rem;
  left: 0.25rem;
  z-index: -1;
  width: calc((100% - 0.5rem) / var(--segment-count));
  border-radius: 0.5rem;
  background: var(--q-primary);
  box-shadow: 0 0.375rem 0.875rem -0.375rem var(--q-primary);
  transform: translateX(calc(var(--segment-index) * 100%));
  transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1);
}

.segmented-toggle__option {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  min-height: 2.25rem;
  padding: 0 0.75rem;
  border: 0;
  border-radius: 0.5rem;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 0.875rem;
  font-weight: 500;
  opacity: 0.7;
  cursor: pointer;
  transition: color 0.25s ease, opacity 0.25s ease;

  &:hover {
    opacity: 1;
  }
}

.segmented-toggle__option--active {
  color: white;
  font-weight: 600;
  opacity: 1;
}
</style>
